<template>
    <div class="sec">
        <div class="sec-main">
            <header class="sec-project-name-bar">
                <span class="sec-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <h1 class="sec-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
            </header>

            <!-- 列表 -->
            <div v-show="view === 'list'" class="sec-panel">
                <div class="sec-toolbar">
                    <el-form :inline="true" class="sec-filter-bar" @submit.native.prevent>
                        <el-form-item>
                            <el-input v-model.trim="keyword" size="small" clearable placeholder="搜索任务名称..."
                                suffix-icon="el-icon-search" style="width: 220px" @input="onKeywordInput"
                                @clear="onFilterChange" />
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="subjectId" size="small" clearable placeholder="全部学科"
                                style="width: 140px" @change="onFilterChange">
                                <el-option label="全部学科" value="" />
                                <el-option v-for="item in subjectOptions" :key="item.id" :label="item.name"
                                    :value="item.id" />
                            </el-select>
                        </el-form-item>
                    </el-form>
                    <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">创建组卷任务</el-button>
                </div>
                <div class="sec-panel-body" v-loading="listLoading">
                    <el-table :data="records" stripe style="width: 100%">
                        <el-table-column label="任务名称" min-width="140" align="center" show-overflow-tooltip>
                            <template slot-scope="{ row }">
                                <span class="sec-name">{{ row.name }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="stageName" label="学段" />
                        <el-table-column prop="subjectName" label="学科" show-overflow-tooltip />
                        <el-table-column prop="gradeName" label="年级" />
                        <el-table-column label="截止时间" width="130">
                            <template slot-scope="{ row }">{{ formatDeadline(row.deadline) }}</template>
                        </el-table-column>
                        <el-table-column label="选择空间" width="200">
                            <template slot-scope="{ row }">
                                <div class="sec-tags">
                                    <span v-for="space in row.spaces || []" :key="space.spxId" class="sec-tag">{{
                                        space.spxName }}</span>
                                    <span v-if="!(row.spaces && row.spaces.length)" class="sec-muted">—</span>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="提交份数" align="center">
                            <template slot-scope="{ row }">{{ formatSubmit(row.submitCount) }}</template>
                        </el-table-column>
                        <el-table-column label="操作" align="center" width="200">
                            <template slot-scope="{ row }">
                                <div class="sec-actions">
                                    <el-button type="text" size="mini" @click="openDetail(row)">查看</el-button>
                                    <el-button type="text" size="mini" :disabled="row.status === 1"
                                        @click="openEdit(row)">编辑</el-button>
                                    <el-button type="text" size="mini" :disabled="row.status === 1"
                                        @click="openPublish(row)">{{ row.status === 1 ? '已发布' : '发布' }}</el-button>
                                    <el-button type="text" size="mini" @click="openDelete(row)">删除</el-button>
                                </div>
                            </template>
                        </el-table-column>
                        <template slot="empty">
                            <el-empty description="暂无组卷任务" />
                        </template>
                    </el-table>
                </div>
                <div v-show="total" class="sec-pagination">
                    <span class="sec-pagination-total">共 {{ total }} 条</span>
                    <pagination :total="total" :page.sync="page" :limit.sync="pageSize" :pageSizes="[10, 20, 30]"
                        @pagination="onPageChange" />
                </div>
            </div>

            <!-- 创建 / 编辑 -->
            <div v-if="view === 'form'" v-loading="detailLoading" class="sec-panel">
                <div class="sec-panel-header">
                    <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回列表</el-button>
                    <div class="sec-panel-title">{{ formMode === 'edit' ? '编辑组卷任务' : '创建组卷任务' }}</div>
                </div>
                <div class="sec-form-panel">
                    <div class="sec-section-title">基本信息</div>
                    <el-form :model="form" label-width="96px" size="small" @submit.native.prevent>
                        <el-form-item label="任务名称" required>
                            <el-input v-model.trim="form.name" maxlength="200" placeholder="如：高二数学期中考试试卷" />
                        </el-form-item>
                        <el-row :gutter="16">
                            <el-col :span="12">
                                <el-form-item label="学段" required>
                                    <el-select v-model="form.stage" placeholder="请选择" style="width: 100%"
                                        @change="onStageChange">
                                        <el-option v-for="item in stageOptions" :key="item.value" :label="item.label"
                                            :value="item.value" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :span="12">
                                <el-form-item label="学科" required>
                                    <el-select v-model="form.subjectId" :disabled="!form.stage"
                                        :placeholder="form.stage ? '请选择学科' : '请先选择学段'" style="width: 100%"
                                        @change="onSubjectChange">
                                        <el-option v-for="item in subjectOptions" :key="item.id" :label="item.name"
                                            :value="item.id" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                        </el-row>
                        <el-form-item label="选择空间" required>
                            <div class="sec-space-row">
                                <el-select v-model="form.spxIds" multiple filterable collapse-tags
                                    :disabled="!form.stage || !form.subjectId" :loading="spaceLoading"
                                    :placeholder="form.stage && form.subjectId ? '请选择子空间' : '请先选择学段和学科'"
                                    style="width: 100%">
                                    <el-option-group v-for="group in spaceGroups" :key="group.key" :label="group.label">
                                        <el-option v-for="item in group.children" :key="item.spxId"
                                            :label="item.displayName || item.spxName" :value="item.spxId" />
                                    </el-option-group>
                                </el-select>
                                <el-button size="small" :disabled="!spaceOptions.length"
                                    @click="selectAllSpaces">全部</el-button>
                            </div>
                            <div class="sec-hint">{{ spaceHint }}</div>
                        </el-form-item>
                        <el-row :gutter="16">
                            <el-col :span="12">
                                <el-form-item label="年级" required>
                                    <el-select v-model="form.grade" :disabled="!form.stage"
                                        :placeholder="form.stage ? '请选择年级' : '请先选择学段'" style="width: 100%">
                                        <el-option v-for="item in gradeOptions" :key="item.value" :label="item.label"
                                            :value="item.value" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :span="12">
                                <el-form-item label="截止时间" required>
                                    <el-date-picker v-model="form.deadline" type="datetime"
                                        value-format="yyyy-MM-dd HH:mm:ss" placeholder="请选择截止时间" style="width: 100%" />
                                </el-form-item>
                            </el-col>
                        </el-row>
                        <el-form-item label="双向细目表" required>
                            <div v-if="showMathTemplate" class="sec-import-head">
                                <el-button type="text" icon="el-icon-download"
                                    @click="downloadTemplate">下载导入模板</el-button>
                            </div>
                            <el-upload v-show="!specs.length" class="sec-upload" drag action="" :show-file-list="false"
                                accept=".xlsx,.xls,.csv" :disabled="importing" :http-request="onImportSpec">
                                <i class="el-icon-document sec-upload-icon"></i>
                                <div class="el-upload__text">点击或拖拽整表文件到此处上传</div>
                                <div class="sec-upload-hint">支持 .xlsx / .xls / .csv，将导入双向细目表全部行内容</div>
                            </el-upload>
                            <div v-if="specs.length" class="sec-file">
                                <i class="el-icon-document"></i>
                                <div class="sec-file-meta">
                                    <div class="sec-file-name">{{ specFileName || '双向细目表' }}</div>
                                    <div class="sec-file-status">已解析 {{ specs.length }} 行，保存后写入任务</div>
                                </div>
                                <el-upload action="" :show-file-list="false" accept=".xlsx,.xls,.csv"
                                    :disabled="importing" :http-request="onImportSpec">
                                    <el-button type="text" size="mini">重新导入</el-button>
                                </el-upload>
                                <el-button type="text" size="mini" @click="clearSpec">移除</el-button>
                            </div>
                            <div class="sec-hint">表头需包含：知识点名称、关键能力名称、学科能力名称、考察要求、测评目标（整表详细内容入库）</div>
                            <!-- <div class="sec-flow">
                                <strong>流转说明：</strong>保存任务后，可在列表页点击「发布」下发到所选空间。发布后空间成员可预览整份双向细目表；空间回调试卷后，教师可对已组习题按入库维度进行标记。
                            </div> -->
                            <div v-if="specs.length" class="sec-preview">
                                <div class="sec-preview-hd">
                                    <span>双向细目表整表预览</span>
                                    <span class="sec-muted">已解析 {{ specs.length }} 行 · 5 类维度</span>
                                </div>
                                <el-table :data="specs" size="mini" max-height="320" border>
                                    <el-table-column prop="questionNo" label="题号" width="72" />
                                    <el-table-column prop="questionType" label="题型" width="88" show-overflow-tooltip />
                                    <el-table-column prop="knowledgeName" label="知识点名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="keyAbilityName" label="关键能力名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="subjectAbilityName" label="学科能力名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="requirement" label="考察要求" width="96" show-overflow-tooltip />
                                    <el-table-column prop="objective" label="测评目标" width="96" show-overflow-tooltip />
                                </el-table>
                                <div class="sec-tags sec-dim-tags">
                                    <span v-for="item in dimensionStats(specs)" :key="item.label" class="sec-tag">{{
                                        item.label }} {{ item.count }}</span>
                                </div>
                            </div>
                        </el-form-item>
                    </el-form>
                    <div class="sec-footer">
                        <el-button size="small" @click="backToList">取消</el-button>
                        <el-button type="primary" size="small" :loading="saving" @click="saveTask">保存</el-button>
                    </div>
                </div>
            </div>

            <!-- 查看 -->
            <div v-if="view === 'detail'" v-loading="detailLoading" class="sec-panel">
                <div class="sec-panel-header">
                    <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回列表</el-button>
                    <div class="sec-panel-title">查看组卷任务</div>
                </div>
                <div class="sec-form-panel">
                    <div class="sec-section-title">基本信息</div>
                    <el-descriptions :column="2" border size="small">
                        <el-descriptions-item label="任务名称" :span="2">{{ detail.name || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="学段">{{ detail.stageName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="学科">{{ detail.subjectName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="年级">{{ detail.gradeName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="截止时间">{{ detail.deadline || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="提交份数">{{ formatSubmit(detailSubmitCount) }}</el-descriptions-item>
                        <el-descriptions-item label="发布状态">{{ detail.statusName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="选择空间" :span="2">
                            <div class="sec-tags">
                                <span v-for="space in detail.spaces || []" :key="space.spxId" class="sec-tag">{{
                                    space.displayName || space.spxName }}</span>
                                <span v-if="!(detail.spaces && detail.spaces.length)" class="sec-muted">—</span>
                            </div>
                        </el-descriptions-item>
                    </el-descriptions>

                    <div class="sec-section-title sec-section-gap">双向细目表</div>
                    <div class="sec-file">
                        <i class="el-icon-document"></i>
                        <div class="sec-file-meta">
                            <div class="sec-file-name">{{ detail.specFileName || '双向细目表' }}</div>
                            <div class="sec-file-status">整表已入库 · 知识点 / 关键能力 / 学科能力 / 考察要求 / 测评目标</div>
                        </div>
                    </div>
                    <el-table :data="detail.specs || []" size="mini" max-height="360" border style="margin-top: 12px">
                        <el-table-column prop="questionNo" label="题号" width="72" />
                        <el-table-column prop="questionType" label="题型" width="88" show-overflow-tooltip />
                        <el-table-column prop="knowledgeName" label="知识点名称" min-width="120" show-overflow-tooltip />
                        <el-table-column prop="keyAbilityName" label="关键能力名称" min-width="120" show-overflow-tooltip />
                        <el-table-column prop="subjectAbilityName" label="学科能力名称" min-width="120"
                            show-overflow-tooltip />
                        <el-table-column prop="requirement" label="考察要求" width="96" show-overflow-tooltip />
                        <el-table-column prop="objective" label="测评目标" width="96" show-overflow-tooltip />
                        <template slot="empty">
                            <el-empty description="暂无细目表" />
                        </template>
                    </el-table>
                    <div class="sec-tags sec-dim-tags">
                        <span v-for="item in dimensionStats(detail.specs || [])" :key="item.label" class="sec-tag">{{
                            item.label }} {{ item.count }}</span>
                    </div>
                    <div class="sec-footer">
                        <el-button size="small" @click="backToList">关闭</el-button>
                        <el-button type="primary" size="small" :disabled="detail.status === 1"
                            @click="openEdit(detail)">去编辑</el-button>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog title="确认发布" :visible.sync="publishVisible" width="420px" append-to-body
            custom-class="sec-confirm-dialog">
            <div class="sec-confirm">确认发布组卷任务「<strong>{{ pendingPublish && pendingPublish.name
            }}</strong>」吗？发布后将下发给所选空间，空间成员可预览双向细目表。</div>
            <span slot="footer">
                <el-button size="small" @click="publishVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="publishing" @click="confirmPublish">确认发布</el-button>
            </span>
        </el-dialog>

        <el-dialog title="确认删除" :visible.sync="deleteVisible" width="420px" append-to-body
            custom-class="sec-confirm-dialog">
            <div class="sec-confirm">确认删除组卷任务「<strong>{{ pendingDelete && pendingDelete.name }}</strong>」吗？删除后不可恢复。
            </div>
            <span slot="footer">
                <el-button size="small" @click="deleteVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="deleting" @click="confirmDelete">确认删除</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import pagination from '@/common/community/pagination.vue';

    const STAGE_OPTIONS = [
        { value: 1, label: '小学' },
        { value: 2, label: '初中' },
        { value: 3, label: '九年一贯制' },
        { value: 4, label: '高中' },
        // { value: 5, label: '幼儿园' },
        // { value: 6, label: '完全中学' },
        { value: 7, label: '十二年一贯制' },
        // { value: 8, label: '大学' },
        // { value: 9, label: '其他' },
        // { value: 10, label: '特殊教育学校' },
        // { value: 11, label: '中等职业教育' },
    ];
    const SUBJECT_OPTIONS = [
        { id: 1, name: '语文' },
        { id: 2, name: '数学' },
        { id: 3, name: '英语' },
        { id: 4, name: '物理' },
        { id: 5, name: '化学' },
        { id: 6, name: '生物学' },
        { id: 7, name: '政治（道法）' },
        { id: 8, name: '历史' },
        { id: 9, name: '地理' },
        { id: 10, name: '科学' },
        { id: 11, name: '历史与社会' },
        { id: 12, name: '信息技术' },
        { id: 13, name: '音乐' },
        { id: 14, name: '美术' },
        { id: 15, name: '体育与健康' },
        { id: 16, name: '通用技术' },
        { id: 17, name: '劳动技术' },
        { id: 18, name: '拓展' },
        { id: 19, name: '心理健康' },
        { id: 20, name: '综合实践活动' },
        { id: 21, name: '日语' },
        { id: 22, name: '俄语' },
        { id: 23, name: '书法' },
        { id: 24, name: '社会' },
        { id: 25, name: '职教专业课' },
        { id: 26, name: '藏语' },
        { id: 27, name: '西班牙语' },
        { id: 28, name: '德语' },
        { id: 29, name: '法语' },
        { id: 30, name: '地方课程' },
    ];
    const GRADE_MAP = {
        1: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
        ],
        2: [
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
        ],
        3: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
        ],
        4: [
            { value: 10, label: '高一' },
            { value: 11, label: '高二' },
            { value: 12, label: '高三' },
        ],
        7: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
            { value: 10, label: '高一' },
            { value: 11, label: '高二' },
            { value: 12, label: '高三' },
        ],
    };
    const DIM_FIELDS = [
        { key: 'knowledgeName', label: '知识点名称' },
        { key: 'keyAbilityName', label: '关键能力名称' },
        { key: 'subjectAbilityName', label: '学科能力名称' },
        { key: 'requirement', label: '考察要求' },
        { key: 'objective', label: '测评目标' },
    ];

    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };
    const isApiOk = (res) => {
        const body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };
    const apiMsg = (res) => {
        const body = unwrapApi(res);
        return (body && body.msg) || '';
    };
    const apiData = (res) => {
        const body = unwrapApi(res);
        return body && body.data;
    };

    const emptyForm = () => ({
        id: null,
        name: '',
        stage: '',
        subjectId: '',
        grade: '',
        deadline: '',
        spxIds: [],
    });

    export default {
        name: 'SpaceExamCompose',
        components: { pagination },
        data() {
            return {
                view: 'list',
                formMode: 'create',
                stageOptions: STAGE_OPTIONS,
                subjectOptions: SUBJECT_OPTIONS,
                keyword: '',
                subjectId: '',
                records: [],
                total: 0,
                page: 1,
                pageSize: 10,
                listLoading: false,
                searchTimer: null,
                form: emptyForm(),
                specs: [],
                specFileName: '',
                spaceOptions: [],
                spaceLoading: false,
                importing: false,
                saving: false,
                hydrating: false,
                detail: {},
                detailSubmitCount: null,
                detailLoading: false,
                publishVisible: false,
                pendingPublish: null,
                publishing: false,
                deleteVisible: false,
                pendingDelete: null,
                deleting: false,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            gradeOptions() {
                return GRADE_MAP[this.form.stage] || [];
            },
            spaceGroups() {
                const map = {};
                const order = [];
                this.spaceOptions.forEach((item) => {
                    const key = item.parentId != null ? String(item.parentId) : (item.parentName || 'other');
                    if (!map[key]) {
                        map[key] = {
                            key,
                            label: item.parentName || '未分组',
                            children: [],
                        };
                        order.push(map[key]);
                    }
                    map[key].children.push(item);
                });
                return order;
            },
            showMathTemplate() {
                return Number(this.form.stage) === 4 && Number(this.form.subjectId) === 2;
            },
            spaceHint() {
                if (!this.form.stage || !this.form.subjectId) {
                    return '请先选择学段、学科，再选择匹配的子空间（可多选）';
                }
                const stage = STAGE_OPTIONS.find((item) => item.value === this.form.stage);
                const subject = this.subjectOptions.find((item) => item.id === this.form.subjectId);
                return '数据来源：本项目下学段为「' + (stage ? stage.label : '') + '」且开设「' + (subject ? subject.name : '') + '」的子空间，可多选';
            },
        },
        watch: {
            projectId() {
                this.view = 'list';
                this.page = 1;
                this.fetchList();
            },
        },
        mounted() {
            this.fetchList();
        },
        beforeDestroy() {
            clearTimeout(this.searchTimer);
        },
        methods: {
            formatDeadline(value) {
                if (!value) return '—';
                return String(value).slice(0, 10);
            },
            formatSubmit(count) {
                if (count === 0 || count) return count + '份';
                return '—';
            },
            dimensionStats(rows) {
                return DIM_FIELDS.map((field) => {
                    const set = {};
                    (rows || []).forEach((row) => {
                        const value = row && row[field.key] != null ? String(row[field.key]).trim() : '';
                        if (value) set[value] = true;
                    });
                    return { label: field.label, count: Object.keys(set).length };
                });
            },
            onKeywordInput() {
                clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(() => {
                    this.onFilterChange();
                }, 300);
            },
            onFilterChange() {
                this.page = 1;
                this.fetchList();
            },
            onPageChange({ pageIndex, pageSize } = {}) {
                if (pageSize) this.pageSize = pageSize;
                this.page = pageIndex || 1;
                this.fetchList();
            },
            async fetchList() {
                if (!this.projectId) return;
                this.listLoading = true;
                try {
                    const params = {
                        projectId: this.projectId,
                        page: this.page,
                        pageSize: this.pageSize,
                    };
                    if (this.keyword) params.name = this.keyword;
                    if (this.subjectId) params.subjectId = this.subjectId;
                    const res = await this.$Api.ExamCompose.getList(params);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '列表加载失败');
                        this.records = [];
                        this.total = 0;
                        return;
                    }
                    const data = apiData(res) || {};
                    this.records = data.records || [];
                    this.total = data.totalCount || 0;
                } catch (e) {
                    this.records = [];
                    this.total = 0;
                    this.$message.error('列表加载失败');
                } finally {
                    this.listLoading = false;
                }
            },
            backToList() {
                this.view = 'list';
                this.fetchList();
            },
            openCreate() {
                this.formMode = 'create';
                this.form = emptyForm();
                this.specs = [];
                this.specFileName = '';
                this.spaceOptions = [];
                this.view = 'form';
            },
            async openEdit(row) {
                if (!row || row.status === 1) {
                    this.$message.warning('任务已发布，不能修改');
                    return;
                }
                this.formMode = 'edit';
                this.view = 'form';
                await this.fillForm(row.id);
            },
            async openDetail(row) {
                this.detailSubmitCount = row ? row.submitCount : null;
                this.view = 'detail';
                this.detailLoading = true;
                try {
                    const res = await this.$Api.ExamCompose.getDetail({ id: row.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '详情加载失败');
                        this.detail = {};
                        return;
                    }
                    this.detail = apiData(res) || {};
                } catch (e) {
                    this.detail = {};
                    this.$message.error('详情加载失败');
                } finally {
                    this.detailLoading = false;
                }
            },
            async fillForm(id) {
                this.detailLoading = true;
                this.hydrating = true;
                try {
                    const res = await this.$Api.ExamCompose.getDetail({ id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '详情加载失败');
                        this.backToList();
                        return;
                    }
                    const detail = apiData(res) || {};
                    if (detail.status === 1) {
                        this.$message.warning('任务已发布，不能修改');
                        this.backToList();
                        return;
                    }
                    this.form = {
                        id: detail.id,
                        name: detail.name || '',
                        stage: detail.stage || '',
                        subjectId: detail.subjectId || '',
                        grade: detail.grade || '',
                        deadline: detail.deadline || '',
                        spxIds: (detail.spaces || []).map((item) => item.spxId),
                    };
                    this.specFileName = detail.specFileName || '';
                    this.specs = (detail.specs || []).map((item) => ({
                        questionNo: item.questionNo,
                        questionType: item.questionType,
                        knowledgeName: item.knowledgeName,
                        keyAbilityName: item.keyAbilityName,
                        subjectAbilityName: item.subjectAbilityName,
                        requirement: item.requirement,
                        objective: item.objective,
                    }));
                    await this.loadSpaces();
                    this.mergeSelectedSpaces(detail.spaces);
                } catch (e) {
                    this.$message.error('详情加载失败');
                    this.backToList();
                } finally {
                    this.hydrating = false;
                    this.detailLoading = false;
                }
            },
            onStageChange() {
                if (this.hydrating) return;
                this.form.subjectId = '';
                this.form.grade = '';
                this.form.spxIds = [];
                this.spaceOptions = [];
            },
            onSubjectChange() {
                if (this.hydrating) return;
                this.form.spxIds = [];
                this.loadSpaces();
            },
            async loadSpaces() {
                if (!this.projectId || !this.form.stage || !this.form.subjectId) {
                    this.spaceOptions = [];
                    return;
                }
                this.spaceLoading = true;
                try {
                    const res = await this.$Api.ExamCompose.getSpaces({
                        projectId: this.projectId,
                        stage: this.form.stage,
                        subjectId: this.form.subjectId,
                    });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '空间加载失败');
                        this.spaceOptions = [];
                        return;
                    }
                    const list = apiData(res);
                    this.spaceOptions = Array.isArray(list) ? list : [];
                } catch (e) {
                    this.spaceOptions = [];
                    this.$message.error('空间加载失败');
                } finally {
                    this.spaceLoading = false;
                }
            },
            mergeSelectedSpaces(spaces) {
                const map = {};
                this.spaceOptions.forEach((item) => {
                    map[item.spxId] = true;
                });
                (spaces || []).forEach((item) => {
                    if (item && item.spxId != null && !map[item.spxId]) {
                        this.spaceOptions.push(item);
                        map[item.spxId] = true;
                    }
                });
            },
            selectAllSpaces() {
                this.form.spxIds = this.spaceOptions.map((item) => item.spxId);
            },
            async downloadTemplate() {
                const href = (process.env.BASE_URL || '/') + 'templates/math.xlsx';
                try {
                    const res = await fetch(href);
                    if (!res.ok) {
                        this.$message.error('模板下载失败');
                        return;
                    }
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '高一2025—2026学年度第一学期学业水平调研（一）-数学试题标签.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                } catch (e) {
                    this.$message.error('模板下载失败');
                }
            },
            async onImportSpec({ file }) {
                const name = (file && file.name) || '';
                if (!/\.(xlsx|xls|csv)$/i.test(name)) {
                    this.$message.error('请上传 .xlsx / .xls / .csv 文件');
                    return;
                }
                this.importing = true;
                try {
                    const res = await this.$Api.ExamCompose.importSpec({ file });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '导入失败');
                        return;
                    }
                    const data = apiData(res) || {};
                    this.specFileName = data.fileName || name;
                    this.specs = data.specs || [];
                    this.$message.success('已解析 ' + (data.rowCount != null ? data.rowCount : this.specs.length) + ' 行');
                } catch (e) {
                    this.$message.error('导入失败');
                } finally {
                    this.importing = false;
                }
            },
            clearSpec() {
                this.specs = [];
                this.specFileName = '';
            },
            async saveTask() {
                if (!this.projectId) {
                    this.$message.warning('请先进入项目');
                    return;
                }
                const name = (this.form.name || '').trim();
                if (!name) {
                    this.$message.warning('请填写任务名称');
                    return;
                }
                if (!this.form.stage) {
                    this.$message.warning('请选择学段');
                    return;
                }
                if (!this.form.subjectId) {
                    this.$message.warning('请选择学科');
                    return;
                }
                if (!this.form.spxIds.length) {
                    this.$message.warning('请选择空间');
                    return;
                }
                if (!this.form.grade) {
                    this.$message.warning('请选择年级');
                    return;
                }
                if (!this.form.deadline) {
                    this.$message.warning('请选择截止时间');
                    return;
                }
                if (!this.specs.length) {
                    this.$message.warning('请导入双向细目表');
                    return;
                }
                const payload = {
                    projectId: this.projectId,
                    name,
                    stage: this.form.stage,
                    subjectId: this.form.subjectId,
                    grade: this.form.grade,
                    deadline: this.form.deadline,
                    spxIds: this.form.spxIds,
                    specFileName: this.specFileName,
                    specs: this.specs.map((item) => ({
                        questionNo: item.questionNo,
                        questionType: item.questionType,
                        knowledgeName: item.knowledgeName,
                        keyAbilityName: item.keyAbilityName,
                        subjectAbilityName: item.subjectAbilityName,
                        requirement: item.requirement,
                        objective: item.objective,
                    })),
                };
                if (this.formMode === 'edit' && this.form.id) payload.id = this.form.id;
                this.saving = true;
                try {
                    const res = await this.$Api.ExamCompose.save(payload);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '保存失败');
                        return;
                    }
                    this.$message.success(this.formMode === 'edit' ? '组卷任务已更新' : '组卷任务已保存，可在列表中发布');
                    this.backToList();
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            },
            openPublish(row) {
                if (!row || row.status === 1) return;
                this.pendingPublish = row;
                this.publishVisible = true;
            },
            async confirmPublish() {
                if (!this.pendingPublish) return;
                this.publishing = true;
                try {
                    const res = await this.$Api.ExamCompose.publish({ id: this.pendingPublish.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '发布失败');
                        return;
                    }
                    this.$message.success('已发布「' + this.pendingPublish.name + '」，空间成员可预览细目表');
                    this.publishVisible = false;
                    this.pendingPublish = null;
                    this.fetchList();
                } catch (e) {
                    this.$message.error('发布失败');
                } finally {
                    this.publishing = false;
                }
            },
            openDelete(row) {
                this.pendingDelete = row;
                this.deleteVisible = true;
            },
            async confirmDelete() {
                if (!this.pendingDelete || !this.pendingDelete.id) return;
                const name = this.pendingDelete.name;
                this.deleting = true;
                try {
                    const res = await this.$Api.ExamCompose.delete({ id: this.pendingDelete.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '删除失败');
                        return;
                    }
                    this.$message.success('已删除「' + (name || '该任务') + '」');
                    this.deleteVisible = false;
                    this.pendingDelete = null;
                    if (this.records.length === 1 && this.page > 1) this.page -= 1;
                    this.fetchList();
                } catch (e) {
                    this.$message.error('删除失败');
                } finally {
                    this.deleting = false;
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-light: #ECF5FF;
    @bg: #F5F7FA;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @border-light: #EBEEF5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .sec {
        min-height: 100%;
    }

    .sec-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .sec-project-name-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .sec-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .sec-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
        flex: 1;
        min-width: 0;
    }

    .sec-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .sec-panel-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 12px;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
    }

    .sec-panel-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .sec-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 16px;
        background: @bg;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;

        >.el-button {
            flex-shrink: 0;
        }
    }

    .sec-filter-bar {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
        margin-bottom: 0;
        flex-wrap: wrap;

        /deep/ .el-form-item {
            margin-bottom: 0;
            margin-right: 10px;
        }
    }

    .sec-panel-body {
        padding: 0;
        min-height: 200px;
    }

    .sec-name {
        color: @text-primary;
        font-weight: 500;
    }

    .sec-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }

    .sec-tag {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 3px;
        font-size: 12px;
        line-height: 18px;
        background: @primary-light;
        color: @primary;
    }

    .sec-muted {
        color: @text-secondary;
        font-size: 12px;
    }

    .sec-actions {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;

        /deep/ .el-button--text {
            padding: 0 4px;
        }
    }

    .sec-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 14px 16px;
        border-top: 1px solid @border-light;

        /deep/ .pagination {
            margin: 0;
        }
    }

    .sec-pagination-total {
        font-size: 13px;
        color: @text-secondary;
        line-height: 30px;
    }

    .sec-form-panel {
        padding: 16px 20px 8px;
    }

    .sec-section-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
        margin-bottom: 16px;
    }

    .sec-section-gap {
        margin-top: 20px;
    }

    .sec-space-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .sec-hint {
        margin-top: 6px;
        font-size: 12px;
        color: @text-secondary;
        line-height: 1.5;
    }

    .sec-import-head {
        display: flex;
        justify-content: flex-end;
        margin-top: -4px;
    }

    .sec-upload {
        width: 100%;

        /deep/ .el-upload,
        /deep/ .el-upload-dragger {
            width: 100%;
        }

        /deep/ .el-upload-dragger {
            height: auto;
            padding: 18px 12px 16px;
        }
    }

    .sec-upload-icon {
        font-size: 28px;
        color: @primary;
        margin-bottom: 6px;
    }

    .sec-upload-hint {
        margin-top: 4px;
        font-size: 12px;
        color: @text-secondary;
    }

    .sec-file {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: #fafafa;

        >i {
            font-size: 22px;
            color: @primary;
        }
    }

    .sec-file-meta {
        flex: 1;
        min-width: 0;
    }

    .sec-file-name {
        font-size: 13px;
        color: @text-primary;
        font-weight: 500;
    }

    .sec-file-status {
        margin-top: 2px;
        font-size: 12px;
        color: @text-secondary;
    }

    .sec-flow {
        margin-top: 8px;
        padding: 8px 10px;
        border-radius: 4px;
        background: @primary-light;
        color: @text-regular;
        font-size: 12px;
        line-height: 1.6;
    }

    .sec-preview {
        margin-top: 12px;
    }

    .sec-preview-hd {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 13px;
        color: @text-primary;
        font-weight: 500;
    }

    .sec-dim-tags {
        margin-top: 8px;
    }

    .sec-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 12px 0 8px;
        border-top: 1px solid @border-light;
    }

    /deep/ .el-table {
        border: none;

        &::before {
            display: none;
        }
    }

    /deep/ .el-table__header thead th {
        color: #000;
        font-weight: 400;
        background-color: RGBA(250, 251, 255, 1);
    }

    /deep/ .el-table__empty-block {
        min-height: 200px;
    }

    .sec-confirm {
        font-size: 14px;
        color: @text-regular;
        line-height: 1.7;
    }
</style>

<style lang="less">
    .sec-confirm-dialog {
        border-radius: 8px;
        overflow: hidden;
    }
</style>
